import { type ReactNode, type SelectHTMLAttributes, useId } from "react";
import { alanBicimi, HATA_BICIMI, YARDIM_BICIMI } from "./alan-bicimi";
import { AlanEtiketi } from "./AlanEtiketi";
import { cn } from "./cn";
import { Ikon } from "./Ikon";

export interface SecimProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  etiket: string;
  /**
   * Etiketi GORSEL olarak gizler — ekran okuyucuda kalir.
   *
   * Tablo hucresindeki kutular icin: sutun basligi zaten "Islem"
   * yaziyor, her satirda bir kez daha yazmak gurultu olurdu. Ama
   * etiketi tamamen kaldirmak kutuyu ekran okuyucuda ISIMSIZ
   * birakirdi — kullanici on tane "secim kutusu" duyar, hangisinin
   * hangi konuya ait oldugunu bilemezdi. O yuzden `etiket` yine
   * ZORUNLU ve satiri tanimlayacak kadar acik olmali.
   */
  etiketGizli?: boolean;
  /**
   * Alan bos birakilamaz: etiketin yaninda "(zorunlu)" + `aria-required`.
   * Yalnizca GERCEK dogrulama kurali olan alana verilir; arama/suzgec
   * kutularina topluca eklemek isareti anlamsizlastirir. Yerli
   * `required` KOYMUYOR — formlar kendi dogrulamasini yapiyor ve
   * tarayici balonu onun mesajinin onune gecerdi.
   */
  zorunlu?: boolean;
  hata?: string;
  yardim?: ReactNode;
  children: ReactNode;
}

/**
 * Etiketli secim kutusu — `Alan`in `<select>` karsiligi.
 *
 * ================================================================
 * NEDEN AYRI BIR BILESEN
 * ================================================================
 * Panelde zaten 17 ham `<select>` var ve ONLAR KUSURLU DEGIL: hepsi
 * `min-h-dokunma` ve `text-base` tasiyor, etiketleri de `<label>`
 * sarmaliyla bagli. Yani bu bilesen bir arizayi kapatmiyor.
 *
 * Eksik olan tek sey HATA DURUMU: ham secim kutusunun kirmizi kenari,
 * `aria-invalid`i ve `aria-describedby` ile bagli hata metni yok.
 * Suzgec kutularinda bu gerekmiyordu — bir suzgec "yanlis" olamaz.
 * KAYIT FORMLARINDA gerekiyor.
 *
 * Bu yuzden mevcut 17 kutu TOPLU CEVRILMEDI. `form-bileseni.test.ts`
 * kendi gerekcesinde "bekci ariza yakalamali, uslup dayatmamali"
 * diyor; calisan ve erisilebilir bir suzgeci yalnizca tekduzelik icin
 * degistirmek o kurala aykiri olurdu.
 *
 * ================================================================
 * SALT OKUNUR HALI YOK — VE OLAMAZ
 * ================================================================
 * `<select>` `readonly` ozelligini TASIMIYOR; HTML'de karsiligi yok.
 * Cizimdeki "salt okunur" hucresi de zaten bir kutu degil, kilitli
 * bir DEGER gosterimi. Secim kutusunda ayni ihtiyacin dogru karsiligi
 * `disabled` — o yuzden burada uydurulmadi.
 */
export function Secim({
  etiket,
  etiketGizli,
  zorunlu = false,
  hata,
  yardim,
  className,
  children,
  ...rest
}: SecimProps) {
  const id = useId();
  const hataId = `${id}-hata`;
  const yardimId = `${id}-yardim`;

  return (
    <div className="flex flex-col gap-1.5">
      <AlanEtiketi htmlFor={id} etiket={etiket} gizli={etiketGizli} zorunlu={zorunlu} />

      <select
        {...rest}
        id={id}
        aria-required={zorunlu || undefined}
        aria-invalid={hata ? true : undefined}
        aria-describedby={cn(hata && hataId, yardim && yardimId) || undefined}
        // Olculer `Alan` ile AYNI: ikisi ayni formda yan yana durunca
        // yuksekligi tutmayan iki kutu satiri kaydiriyor.
        className={alanBicimi({
          hata: Boolean(hata),
          dokunmaHedefi: true,
          ekSinif: className,
        })}
      >
        {children}
      </select>

      {yardim && (
        <p id={yardimId} className={YARDIM_BICIMI}>
          {yardim}
        </p>
      )}
      {hata && (
        <p id={hataId} role="alert" className={HATA_BICIMI}>
          <Ikon ad="hata" boyut="kucuk" className="mt-px" />
          {hata}
        </p>
      )}
    </div>
  );
}
